import React, { useState } from 'react';
import AdminLayout from '../AdminLayout';
import './ControlsPage.css';
import { useGetTradeFeeQuery, useUpdateTradeFeeMutation, useGetMonthlyFeeQuery, useUpdateMonthlyFeeMutation, useGetActiveTraderDiscountQuery, useUpdateActiveTraderDiscountMutation, useGetMaintenanceModeQuery, useUpdateMaintenanceModeMutation } from 'store/services/admin/adminApi';
import { useGetAdminEconomicSettingsQuery, useUpdateAdminEconomicSettingsMutation } from 'store/services/economic/economicApi';
import { toast } from 'react-toastify';
import { feeExample, formatFeePct } from 'config/economyCopy';
import MagicButton from 'components/MagicButton/MagicButton';
import ConfirmMaintenanceModal from 'components/ConfirmMaintenanceModal/ConfirmMaintenanceModal';
import LanguageMultiplierSection from './LanguageMultiplierSection';
import RegistrationAllowlistSection from './RegistrationAllowlistSection';

const ControlsPage = () => {
  const { data: tradeFeeData, isLoading } = useGetTradeFeeQuery();
  const [updateTradeFee] = useUpdateTradeFeeMutation();
  const { data: monthlyFeeData, isLoading: isLoadingMonthlyFee } = useGetMonthlyFeeQuery();
  const [updateMonthlyFee] = useUpdateMonthlyFeeMutation();
  const { data: activeTraderDiscountData, isLoading: isLoadingDiscount } = useGetActiveTraderDiscountQuery();
  const [updateActiveTraderDiscount] = useUpdateActiveTraderDiscountMutation();
  const { data: targetPointsData, isLoading: isLoadingTargetPoints } = useGetAdminEconomicSettingsQuery();
  const [updateTargetPoints] = useUpdateAdminEconomicSettingsMutation();
  const { data: maintenanceModeData, isLoading: isLoadingMaintenance } = useGetMaintenanceModeQuery(undefined, {
    pollingInterval: 60000, // Poll every 60 seconds
  });
  const [updateMaintenanceMode, { isLoading: isUpdatingMaintenance }] = useUpdateMaintenanceModeMutation();

  const [isEditing, setIsEditing] = useState(false);
  const [newFeePercentage, setNewFeePercentage] = useState<string>('');
  const [isEditingMonthly, setIsEditingMonthly] = useState(false);
  const [newMonthlyFee, setNewMonthlyFee] = useState<string>('');
  const [isEditingDiscount, setIsEditingDiscount] = useState(false);
  const [newDiscount, setNewDiscount] = useState<string>('');
  const [isEditingTargetPoints, setIsEditingTargetPoints] = useState(false);
  const [newTargetPoints, setNewTargetPoints] = useState<string>('');
  const [isConfirmMaintenanceModalOpen, setIsConfirmMaintenanceModalOpen] = useState(false);

  const handleEdit = () => {
    setNewFeePercentage(tradeFeeData?.tradeFeePercentage?.toString() ?? '');
    setIsEditing(true);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setNewFeePercentage('');
  };

  const handleSave = async () => {
    const percentage = parseFloat(newFeePercentage);

    // Validate input
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      toast.error('Trade fee percentage must be between 0 and 100');
      return;
    }

    try {
      const result = await updateTradeFee({
        tradeFeePercentage: percentage
      }).unwrap();

      if (result.success) {
        toast.success(`Trade fee updated to ${percentage}%`);
        setIsEditing(false);
      }
    } catch (error: any) {
      console.error('Failed to update trade fee:', error);
      toast.error(error?.data?.message || 'Failed to update trade fee');
    }
  };

  const handleEditMonthly = () => {
    setNewMonthlyFee(monthlyFeeData?.monthlyFeePoints?.toString() || '10');
    setIsEditingMonthly(true);
  };

  const handleCancelMonthly = () => {
    setIsEditingMonthly(false);
    setNewMonthlyFee('');
  };

  const handleSaveMonthly = async () => {
    const points = parseInt(newMonthlyFee, 10);

    // Validate input - must be integer between 0 and 10000
    if (isNaN(points) || points < 0 || points > 10000 || !Number.isInteger(parseFloat(newMonthlyFee))) {
      toast.error('Monthly fee must be a whole number between 0 and 10,000');
      return;
    }

    try {
      const result = await updateMonthlyFee({
        monthlyFeePoints: points
      }).unwrap();

      if (result.success) {
        toast.success(`Monthly fee updated to ${points} points`);
        setIsEditingMonthly(false);
      }
    } catch (error: any) {
      console.error('Failed to update monthly fee:', error);
      toast.error(error?.data?.message || 'Failed to update monthly fee');
    }
  };

  const handleEditDiscount = () => {
    setNewDiscount(activeTraderDiscountData?.activeTraderDiscountPercentage?.toString() || '50');
    setIsEditingDiscount(true);
  };

  const handleCancelDiscount = () => {
    setIsEditingDiscount(false);
    setNewDiscount('');
  };

  const handleSaveDiscount = async () => {
    const percentage = parseFloat(newDiscount);

    // Validate input - must be number between 0 and 100
    if (isNaN(percentage) || percentage < 0 || percentage > 100) {
      toast.error('Active trader discount must be between 0 and 100%');
      return;
    }

    try {
      const result = await updateActiveTraderDiscount({
        activeTraderDiscountPercentage: percentage
      }).unwrap();

      if (result.success) {
        toast.success(`Active trader discount updated to ${percentage}%`);
        setIsEditingDiscount(false);
      }
    } catch (error: any) {
      console.error('Failed to update active trader discount:', error);
      toast.error(error?.data?.message || 'Failed to update active trader discount');
    }
  };

  const handleEditTargetPoints = () => {
    setNewTargetPoints(targetPointsData?.targetPointsPerActiveTrader?.toString() || '3000');
    setIsEditingTargetPoints(true);
  };

  const handleCancelTargetPoints = () => {
    setIsEditingTargetPoints(false);
    setNewTargetPoints('');
  };

  const handleSaveTargetPoints = async () => {
    const points = parseInt(newTargetPoints, 10);

    // Validate input - must be integer between 0 and 100000
    if (isNaN(points) || points < 0 || points > 100000 || !Number.isInteger(parseFloat(newTargetPoints))) {
      toast.error('Target points must be a whole number between 0 and 100,000');
      return;
    }

    try {
      await updateTargetPoints({
        targetPointsPerActiveTrader: points
      }).unwrap();

      toast.success(`Target points per active trader updated to ${points.toLocaleString()}`);
      setIsEditingTargetPoints(false);
    } catch (error: any) {
      console.error('Failed to update target points:', error);
      toast.error(error?.data?.message || 'Failed to update target points');
    }
  };

  const handleToggleMaintenance = () => {
    const isCurrentlyEnabled = maintenanceModeData?.maintenanceMode || false;

    if (!isCurrentlyEnabled) {
      // Show confirmation modal before enabling
      setIsConfirmMaintenanceModalOpen(true);
    } else {
      // Disable immediately without confirmation
      handleConfirmMaintenanceToggle(false);
    }
  };

  const handleConfirmMaintenanceToggle = async (enabled?: boolean) => {
    const targetState = enabled !== undefined ? enabled : !maintenanceModeData?.maintenanceMode;

    try {
      const result = await updateMaintenanceMode({
        enabled: targetState
      }).unwrap();

      if (result.success) {
        toast.success(targetState ? 'Maintenance mode enabled' : 'Maintenance mode disabled');
        setIsConfirmMaintenanceModalOpen(false);
      }
    } catch (error: any) {
      console.error('Failed to update maintenance mode:', error);
      toast.error(error?.data?.message || 'Failed to update maintenance mode');
      setIsConfirmMaintenanceModalOpen(false);
    }
  };

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <AdminLayout title="Admin Panel - Controls" currentPath="/admin/controls">
      <div className="controls-page">
        <div className="controls-content">
          <h1 className="controls-title">Controls</h1>

          <div className="controls-card">
            <div className="card-header">
              <h2>Trade Fee Settings</h2>
              <p className="card-description">
                Configure the percentage fee applied to all completed trades
              </p>
            </div>

            <div className="card-content">
              {isLoading ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <>
                  <div className="fee-display">
                    <label className="fee-label">Trade Fee:</label>
                    {isEditing ? (
                      <div className="edit-container">
                        <input
                          type="number"
                          className="fee-input"
                          value={newFeePercentage}
                          onChange={(e) => setNewFeePercentage(e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="Enter percentage"
                          autoFocus
                        />
                        <span className="percentage-symbol">%</span>
                      </div>
                    ) : (
                      <div className="fee-value">
                        <span className="fee-percentage">
                          {/* `!= null`, not `||`: a real 0% must read 0%, and an
                              unloaded value shows nothing rather than "1%". */}
                          {tradeFeeData?.tradeFeePercentage != null
                            ? formatFeePct(tradeFeeData.tradeFeePercentage)
                            : '—'}
                        </span>
                      </div>
                    )}
                  </div>

                  {tradeFeeData?.updatedAt && !isEditing && (
                    <div className="last-updated">
                      Last updated: {formatDate(tradeFeeData.updatedAt)}
                    </div>
                  )}

                  <div className="button-container">
                    {isEditing ? (
                      <>
                        <MagicButton
                          label="Save"
                          color="purple"
                          onPress={handleSave}
                        />
                        <MagicButton
                          label="Cancel"
                          color="grey"
                          onPress={handleCancel}
                        />
                      </>
                    ) : (
                      <MagicButton
                        label="Edit"
                        color="purple"
                        onPress={handleEdit}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="controls-card">
            <div className="card-header">
              <h2>Monthly Point Fee Settings</h2>
              <p className="card-description">
                Configure the point fee automatically deducted from all users monthly
              </p>
            </div>

            <div className="card-content">
              {isLoadingMonthlyFee || isLoadingDiscount ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <>
                  <div className="fee-display">
                    <label className="fee-label">Monthly Fee:</label>
                    {isEditingMonthly ? (
                      <div className="edit-container">
                        <input
                          type="number"
                          className="fee-input"
                          value={newMonthlyFee}
                          onChange={(e) => setNewMonthlyFee(e.target.value)}
                          min="0"
                          max="10000"
                          step="1"
                          placeholder="Enter points"
                          autoFocus
                        />
                        <span className="points-label">points</span>
                      </div>
                    ) : (
                      <div className="fee-value">
                        <span className="fee-points">
                          {monthlyFeeData?.monthlyFeePoints ?? 10}
                        </span>
                        <span className="points-label">points</span>
                      </div>
                    )}
                  </div>

                  {monthlyFeeData?.updatedAt && !isEditingMonthly && (
                    <div className="last-updated">
                      Last updated: {formatDate(monthlyFeeData.updatedAt)}
                    </div>
                  )}

                  <div className="button-container">
                    {isEditingMonthly ? (
                      <>
                        <MagicButton
                          label="Save"
                          color="purple"
                          onPress={handleSaveMonthly}
                        />
                        <MagicButton
                          label="Cancel"
                          color="grey"
                          onPress={handleCancelMonthly}
                        />
                      </>
                    ) : (
                      <MagicButton
                        label="Edit"
                        color="purple"
                        onPress={handleEditMonthly}
                      />
                    )}
                  </div>

                  <div className="fee-display fee-display-divider">
                    <label className="fee-label">Active Trader Discount:</label>
                    {isEditingDiscount ? (
                      <div className="edit-container">
                        <input
                          type="number"
                          className="fee-input"
                          value={newDiscount}
                          onChange={(e) => setNewDiscount(e.target.value)}
                          min="0"
                          max="100"
                          step="0.1"
                          placeholder="Enter discount %"
                          autoFocus
                        />
                        <span className="percentage-symbol">%</span>
                      </div>
                    ) : (
                      <div className="fee-value">
                        <span className="fee-percentage">
                          {activeTraderDiscountData?.activeTraderDiscountPercentage ?? 50}%
                        </span>
                      </div>
                    )}
                  </div>

                  {activeTraderDiscountData?.updatedAt && !isEditingDiscount && (
                    <div className="last-updated">
                      Last updated: {formatDate(activeTraderDiscountData.updatedAt)}
                    </div>
                  )}

                  <div className="button-container">
                    {isEditingDiscount ? (
                      <>
                        <MagicButton
                          label="Save"
                          color="purple"
                          onPress={handleSaveDiscount}
                        />
                        <MagicButton
                          label="Cancel"
                          color="grey"
                          onPress={handleCancelDiscount}
                        />
                      </>
                    ) : (
                      <MagicButton
                        label="Edit"
                        color="purple"
                        onPress={handleEditDiscount}
                      />
                    )}
                  </div>

                  <div className="discount-info">
                    <strong>Active traders pay:</strong> {Math.round((monthlyFeeData?.monthlyFeePoints ?? 10) * (1 - (activeTraderDiscountData?.activeTraderDiscountPercentage ?? 50) / 100))} points
                    ({activeTraderDiscountData?.activeTraderDiscountPercentage ?? 50}% discount applied)
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="controls-card">
            <div className="card-header">
              <h2>Target Points Per Active Trader</h2>
              <p className="card-description">
                Configure the target number of points per active trader for optimal economy health
              </p>
            </div>

            <div className="card-content">
              {isLoadingTargetPoints ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <>
                  <div className="fee-display">
                    <label className="fee-label">Target Points:</label>
                    {isEditingTargetPoints ? (
                      <div className="edit-container">
                        <input
                          type="number"
                          className="fee-input"
                          value={newTargetPoints}
                          onChange={(e) => setNewTargetPoints(e.target.value)}
                          min="0"
                          max="100000"
                          step="100"
                          placeholder="Enter target points"
                          autoFocus
                        />
                        <span className="points-label">points</span>
                      </div>
                    ) : (
                      <div className="fee-value">
                        <span className="fee-points">
                          {(targetPointsData?.targetPointsPerActiveTrader ?? 3000).toLocaleString()}
                        </span>
                        <span className="points-label">points</span>
                      </div>
                    )}
                  </div>

                  {targetPointsData?.updatedAt && !isEditingTargetPoints && (
                    <div className="last-updated">
                      Last updated: {formatDate(targetPointsData.updatedAt)}
                    </div>
                  )}

                  <div className="button-container">
                    {isEditingTargetPoints ? (
                      <>
                        <MagicButton
                          label="Save"
                          color="purple"
                          onPress={handleSaveTargetPoints}
                        />
                        <MagicButton
                          label="Cancel"
                          color="grey"
                          onPress={handleCancelTargetPoints}
                        />
                      </>
                    ) : (
                      <MagicButton
                        label="Edit"
                        color="purple"
                        onPress={handleEditTargetPoints}
                      />
                    )}
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="controls-card">
            <div className="card-header">
              <h2>Maintenance Mode</h2>
              <p className="card-description">
                Enable maintenance mode to prevent non-admin users from accessing the site
              </p>
            </div>

            <div className="card-content">
              {isLoadingMaintenance ? (
                <div className="loading-text">Loading...</div>
              ) : (
                <>
                  <div className="fee-display">
                    <label className="fee-label">Status:</label>
                    <div className="fee-value">
                      <span className={`status-indicator ${maintenanceModeData?.maintenanceMode ? 'active' : 'inactive'}`}>
                        {maintenanceModeData?.maintenanceMode ? 'ENABLED' : 'DISABLED'}
                      </span>
                    </div>
                  </div>

                  {maintenanceModeData?.maintenanceMode && maintenanceModeData?.maintenanceModeEnabledAt && (
                    <div className="last-updated">
                      Enabled at: {formatDate(maintenanceModeData.maintenanceModeEnabledAt)}
                    </div>
                  )}

                  {maintenanceModeData?.maintenanceMode && (
                    <div className="maintenance-warning">
                      Warning: Maintenance mode is active. Regular users cannot access the site. You have admin access.
                    </div>
                  )}

                  <div className="button-container">
                    <MagicButton
                      label={maintenanceModeData?.maintenanceMode ? "Disable Maintenance Mode" : "Enable Maintenance Mode"}
                      color={maintenanceModeData?.maintenanceMode ? "grey" : "purple"}
                      onPress={handleToggleMaintenance}
                      isLoading={isUpdatingMaintenance}
                      isDisabled={isUpdatingMaintenance}
                    />
                  </div>
                </>
              )}
            </div>
          </div>

          <div className="controls-info">
            <h3>How Trade Fees Work</h3>
            <ul>
              <li>When a trade is completed, the fee percentage is automatically deducted from the points credited to the sender</li>
              {tradeFeeData?.tradeFeePercentage != null && (
                <li>
                  Example: With the current {formatFeePct(tradeFeeData.tradeFeePercentage)} fee, a 365 point trade
                  credits {365 - feeExample(365, tradeFeeData.tradeFeePercentage)} points to the sender (
                  {feeExample(365, tradeFeeData.tradeFeePercentage)} points fee)
                </li>
              )}
              <li>Fees are always rounded down to the nearest whole point</li>
              <li>No fee is applied to cancelled trades - full refunds are issued</li>
              <li>Changes take effect immediately for all new trade completions</li>
            </ul>
          </div>

          <div className="controls-info">
            <h3>How Monthly Fees Work</h3>
            <ul>
              <li>Monthly fees are charged on each user's account anniversary date (one month after signup)</li>
              <li>The fee is automatically deducted from the user's point balance</li>
              <li>If a user has fewer points than the fee amount, their balance is reduced to 0 (users cannot have negative points)</li>
              <li>Changes to the monthly fee amount take effect immediately for all future charges</li>
              <li>The system has a 7-day grace period - if a fee cannot be collected on the anniversary date, the system will retry for up to 7 days</li>
              <li>After the grace period expires, that month's fee is forgiven and the next charge is scheduled for the following month</li>
              <li>No retroactive charges - changes only affect future billing cycles</li>
            </ul>

            <h3 style={{ marginTop: '24px' }}>How Active Trader Discount Works</h3>
            <ul>
              <li><strong>Active traders</strong> are users who have sent or received at least one trade, or created an account in the last 30 days</li>
              <li>Active traders receive a discount on their monthly fee, reducing their platform cost of participation</li>
              <li>The discount is applied automatically when the monthly fee is charged</li>
              <li>Example: With a 50% discount and 10 point monthly fee, active traders pay only 5 points</li>
              <li>Inactive accounts pay the full monthly fee, creating an incentive to engage with the platform</li>
              <li>This mechanism helps reclaim points from dormant accounts while rewarding engaged community members</li>
              <li>The discount percentage can be adjusted from 0-100% and takes effect immediately for all future fee collections</li>
              <li>The discount percentage is displayed publicly on the Sustainable Economics article page</li>
            </ul>
          </div>

          <div className="controls-info">
            <h3>How Target Points Per Active Trader Works</h3>
            <ul>
              <li>This target represents the ideal number of points each active trader should have for optimal economy health</li>
              <li>Active traders are defined as users who have sent or received trades, or created an account in the last 30 days</li>
              <li>The system uses this target to calculate economic health metrics and guide fee adjustments</li>
              <li>This value is displayed publicly on the Sustainable Economics article page as part of the transparency dashboard</li>
              <li>Recommended range: 2,000-5,000 points per active trader for healthy trade velocity</li>
              <li>Changes take effect immediately and are reflected in the public economic dashboard</li>
              <li>This is a target/goal metric - not an enforced limit or balance requirement for users</li>
            </ul>
          </div>
        </div>
      </div>

      <LanguageMultiplierSection />

      <RegistrationAllowlistSection />

      <ConfirmMaintenanceModal
        isOpen={isConfirmMaintenanceModalOpen}
        onClose={() => setIsConfirmMaintenanceModalOpen(false)}
        onConfirm={() => handleConfirmMaintenanceToggle(true)}
        isLoading={isUpdatingMaintenance}
      />
    </AdminLayout>
  );
};

export default ControlsPage;
